import { dashboardLayoutForUser, isDualRoleAccount } from './accountRole';
import {
  switchToOwnListeningProfileRoute,
  switchToOwnTalkingProfileRoute,
} from './ownListeningProfile';

export const NAV_MODE_TALKER = 'talker';
export const NAV_MODE_LISTENER = 'listener';
export const NAV_MODES = [NAV_MODE_TALKER, NAV_MODE_LISTENER];

/** Last-used Talker / Listener menu. Stored on protectedData.preferred. */
export const PREFERRED_NAV_MODE_KEY = 'preferred';

const protectedDataOf = currentUser => currentUser?.attributes?.profile?.protectedData || {};
const privateDataOf = currentUser => currentUser?.attributes?.profile?.privateData || {};

/**
 * Normalize a stored preferred menu mode.
 *
 * @param {*} raw
 * @returns {'talker'|'listener'|null}
 */
export const normalizePreferredNavMode = raw => {
  if (raw === NAV_MODE_TALKER || raw === 'talk') {
    return NAV_MODE_TALKER;
  }
  if (raw === NAV_MODE_LISTENER || raw === 'listen') {
    return NAV_MODE_LISTENER;
  }
  return null;
};

/**
 * Last-used hamburger mode from protectedData.preferred.
 *
 * @param {Object} [currentUser]
 * @returns {'talker'|'listener'|null}
 */
export const getStoredPreferredNavMode = currentUser =>
  normalizePreferredNavMode(protectedDataOf(currentUser)[PREFERRED_NAV_MODE_KEY]) ||
  normalizePreferredNavMode(privateDataOf(currentUser)[PREFERRED_NAV_MODE_KEY]);

/**
 * Dashboard / Profile tab that matches a hamburger mode.
 *
 * @param {'talker'|'listener'} mode
 * @returns {'talking'|'listening'}
 */
export const dashboardTabForNavMode = mode => (mode === NAV_MODE_TALKER ? 'talking' : 'listening');

/**
 * Inbox list that matches a hamburger mode.
 *
 * @param {'talker'|'listener'} mode
 * @returns {'orders'|'sales'}
 */
export const inboxTabForNavMode = mode => (mode === NAV_MODE_TALKER ? 'orders' : 'sales');

export const TIPS_TAB_LISTENER = 'listener';
export const TIPS_TAB_TALKER = 'talker';

/**
 * Listener or Talker tab from /tips?tab=. Missing or unknown values use
 * `fallbackMode` when provided, otherwise Listener.
 *
 * @param {string} [search]
 * @param {'talker'|'listener'} [fallbackMode]
 * @returns {'listener'|'talker'}
 */
export const tipsTabFromSearch = (search, fallbackMode) => {
  const raw = typeof search === 'string' ? search : '';
  const query = new URLSearchParams(raw.charAt(0) === '?' ? raw.slice(1) : raw);
  const tab = query.get('tab');
  if (tab === TIPS_TAB_TALKER) {
    return TIPS_TAB_TALKER;
  }
  if (tab === TIPS_TAB_LISTENER) {
    return TIPS_TAB_LISTENER;
  }
  return fallbackMode === NAV_MODE_TALKER ? TIPS_TAB_TALKER : TIPS_TAB_LISTENER;
};

/**
 * Menu search that opens Tips & Resources on the current mode’s tab.
 *
 * @param {'talker'|'listener'} mode
 * @returns {string}
 */
export const tipsSearchForNavMode = mode =>
  `?tab=${mode === NAV_MODE_TALKER ? TIPS_TAB_TALKER : TIPS_TAB_LISTENER}`;

const ACCOUNT_SETTINGS_SWITCH_PAGES = [
  'ManageAccountPage',
  'AccountSettingsPage',
  'ContactDetailsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'PaymentMethodsPage',
];

const SHARED_SITE_SWITCH_PAGES = [
  'LandingPage',
  'HowItWorksPage',
  'ForTalkersPage',
  'ForListenersPage',
  'AboutPage',
  'SafetyPage',
  'FAQPage',
  'PricingPage',
  'ContactPage',
  'HelpPage',
];

const PROFILE_SWITCH_PAGES = [
  'YourProfilePage',
  'YourProfilePageTab',
  'ProfileSettingsPage',
  'AccountProfilePage',
];

const OWN_PROFILE_SWITCH_PAGES = [
  'ProfilePage',
  'ListingPage',
  'ListingPageVariant',
  'ListingPageCanonical',
];

const locationResult = ({ name, search = '', searchParams = {}, params = {}, hash = '' }) => ({
  name,
  search,
  searchParams,
  params,
  hash,
});

const dashboardSwitchLocation = (mode, hash = '') =>
  locationResult({
    name: 'DashboardPage',
    search: `?tab=${dashboardTabForNavMode(mode)}`,
    searchParams: { tab: dashboardTabForNavMode(mode) },
    hash,
  });

/**
 * Where the dual-role toggle should land. Same page in the other mode when
 * that page exists on both sides (Tips, Inbox, Calendar, Favorites,
 * dashboard, profile, account settings). One-mode pages (Promote, Search,
 * listing, sale/order details) fall back to that mode's dashboard.
 * Own Talking / Listening profile pages switch to the other profile
 * (`/u/:id` or `/l/:slug/:id`), or that profile's setup prompt.
 *
 * @param {'talker'|'listener'} mode
 * @param {Object} [context]
 * @param {string} [context.currentPage]
 * @param {string} [context.search]
 * @param {string} [context.hash]
 * @param {string} [context.inboxTab]
 * @param {Object} [context.currentUser]
 * @param {Object} [context.currentUserOwnListing]
 * @returns {{ name: string, search: string, searchParams: Object, params: Object, hash: string }}
 */
export const switchNavModeLocation = (mode, context = {}) => {
  const next = mode === NAV_MODE_TALKER ? NAV_MODE_TALKER : NAV_MODE_LISTENER;
  const page = pageNameOf(context.currentPage);
  const fragment = typeof context.hash === 'string' ? context.hash.replace(/^#/, '') : '';

  if (page === 'TipsPage') {
    const tab = next === NAV_MODE_TALKER ? TIPS_TAB_TALKER : TIPS_TAB_LISTENER;
    return locationResult({
      name: 'TipsPage',
      search: tipsSearchForNavMode(next),
      searchParams: { tab },
    });
  }
  if (page === 'FavoritesPage') {
    return locationResult({ name: 'FavoritesPage' });
  }
  if (page === 'InboxPage' || page === 'SessionsCalendarPage') {
    const tab = inboxTabForNavMode(next);
    return locationResult({
      name: 'InboxPage',
      params: { tab },
      search: context.search && /view=/.test(context.search) ? context.search : undefined,
    });
  }
  if (page === 'DashboardPage') {
    return dashboardSwitchLocation(next, fragment === 'favorites' ? '#favorites' : '');
  }
  if (OWN_PROFILE_SWITCH_PAGES.includes(page)) {
    const dest =
      next === NAV_MODE_TALKER
        ? switchToOwnTalkingProfileRoute(context.currentUser)
        : switchToOwnListeningProfileRoute(context.currentUserOwnListing);
    return locationResult({
      name: dest.name,
      params: dest.params || {},
      search: dest.search || '',
      searchParams: dest.searchParams || {},
    });
  }
  if (PROFILE_SWITCH_PAGES.includes(page)) {
    return locationResult({
      name: 'YourProfilePage',
      search: `?tab=${dashboardTabForNavMode(next)}`,
      searchParams: { tab: dashboardTabForNavMode(next) },
    });
  }
  if (ACCOUNT_SETTINGS_SWITCH_PAGES.includes(page)) {
    return locationResult({
      name: page === 'AccountSettingsPage' ? 'ContactDetailsPage' : page,
    });
  }
  if (SHARED_SITE_SWITCH_PAGES.includes(page)) {
    const rawSearch = typeof context.search === 'string' ? context.search : '';
    return locationResult({
      name: page,
      search: rawSearch,
    });
  }

  return dashboardSwitchLocation(next);
};

/**
 * Dual-role accounts can switch Talker / Listener menus.
 *
 * @param {Object} [params]
 * @returns {boolean}
 */
export const canSwitchNavMode = (params = {}) => isDualRoleAccount(params);

const pageNameOf = currentPage =>
  typeof currentPage === 'string' ? currentPage.split(':')[0] : '';
const inboxTabOf = currentPage => {
  if (typeof currentPage !== 'string') {
    return null;
  }
  const parts = currentPage.split(':');
  return parts[0] === 'InboxPage' && parts[1] ? parts[1] : null;
};
const searchParamsOf = search => {
  const raw = typeof search === 'string' ? search : '';
  return new URLSearchParams(raw.charAt(0) === '?' ? raw.slice(1) : raw);
};

/**
 * Page-forced hamburger mode. Listener-side routes win over a saved Talker
 * preference, and the reverse. Neutral pages return null so Switch’s default
 * is used.
 *
 * @param {Object} [params]
 * @param {string} [params.currentPage]
 * @param {string} [params.search]
 * @param {string} [params.inboxTab]
 * @returns {'talker'|'listener'|null}
 */
export const navModeForPage = ({ currentPage, search, inboxTab, isOwnListing } = {}) => {
  const page = pageNameOf(currentPage);
  const query = searchParamsOf(search);
  const tab = query.get('tab');
  const role = query.get('role');
  const edit = query.get('edit');
  const inbox = inboxTabOf(currentPage) || inboxTab;

  if (page === 'SaleDetailsPage' || page === 'SaleDetailsPageRedirect') {
    return NAV_MODE_LISTENER;
  }
  if (page === 'OrderDetailsPage' || page === 'OrderDetailsPageRedirect') {
    return NAV_MODE_TALKER;
  }
  if (page === 'SearchPage' || page === 'SearchPageWithListingType') {
    return NAV_MODE_TALKER;
  }
  if (page === 'ProfilePage') {
    return NAV_MODE_TALKER;
  }
  if (page === 'ListingPage' || page === 'ListingPageVariant' || page === 'ListingPageCanonical') {
    if (isOwnListing) {
      return NAV_MODE_LISTENER;
    }
    return NAV_MODE_TALKER;
  }
  if (page === 'SessionsCalendarPage') {
    if (role === 'talking') {
      return NAV_MODE_TALKER;
    }
    if (role === 'listening') {
      return NAV_MODE_LISTENER;
    }
    return null;
  }
  if (
    page === 'ListenerEarningsPage' ||
    page === 'AvailabilityPage' ||
    page === 'NewListingPage' ||
    page === 'EditListingPage' ||
    page === 'PromotePage'
  ) {
    return NAV_MODE_LISTENER;
  }
  if (page === 'InboxPage') {
    if (inbox === 'orders') {
      return NAV_MODE_TALKER;
    }
    if (inbox === 'sales') {
      return NAV_MODE_LISTENER;
    }
  }
  if (page === 'DashboardPage') {
    if (tab === 'listening') {
      return NAV_MODE_LISTENER;
    }
    if (tab === 'talking') {
      return NAV_MODE_TALKER;
    }
    return null;
  }
  if (page === 'TipsPage') {
    if (tab === TIPS_TAB_LISTENER) {
      return NAV_MODE_LISTENER;
    }
    if (tab === TIPS_TAB_TALKER) {
      return NAV_MODE_TALKER;
    }
    return null;
  }
  if (
    page === 'YourProfilePage' ||
    page === 'YourProfilePageTab' ||
    page === 'ProfileSettingsPage' ||
    page === 'AccountProfilePage'
  ) {
    if (tab === 'listening') {
      return NAV_MODE_LISTENER;
    }
    if (tab === 'talking' || edit === 'talking') {
      return NAV_MODE_TALKER;
    }
    return null;
  }
  return null;
};

/**
 * Menu mode for this account. Single-role accounts stay on that side.
 * Dual-role follows the current page when it is listener- or talker-sided,
 * otherwise the last stored mode (Listener by default).
 *
 * @param {Object} [params]
 * @returns {'talker'|'listener'}
 */
export const resolveNavMode = (params = {}) => {
  const { showListening, showTalking } = dashboardLayoutForUser(params);
  if (showListening && !showTalking) {
    return NAV_MODE_LISTENER;
  }
  if (showTalking && !showListening) {
    return NAV_MODE_TALKER;
  }
  return (
    navModeForPage(params) || getStoredPreferredNavMode(params.currentUser) || NAV_MODE_LISTENER
  );
};

/**
 * Favorites are talker-only. Uses the saved Talker/Listener preference so a
 * listing page that page-forces Talker chrome does not show hearts or the
 * saved-listeners list while the account is in Listener mode.
 *
 * @param {Object} [params]
 * @param {Object} [params.currentUser]
 * @param {boolean} [params.currentUserHasListings]
 * @returns {boolean}
 */
export const isFavoritesTalkerMode = (params = {}) =>
  clampNavModeToAccount(getStoredPreferredNavMode(params.currentUser), params) === NAV_MODE_TALKER;

/**
 * Single-role accounts stay on that side even if a caller passes the other
 * mode (page-role forcing, leftover preferred). Dual-role keeps `mode`.
 *
 * @param {'talker'|'listener'|null|undefined} mode
 * @param {Object} [params]
 * @returns {'talker'|'listener'}
 */
export const clampNavModeToAccount = (mode, params = {}) => {
  const { showListening, showTalking } = dashboardLayoutForUser(params);
  if (showListening && !showTalking) {
    return NAV_MODE_LISTENER;
  }
  if (showTalking && !showListening) {
    return NAV_MODE_TALKER;
  }
  if (mode === NAV_MODE_TALKER || mode === NAV_MODE_LISTENER) {
    return mode;
  }
  return resolveNavMode(params);
};

/**
 * Opposite mode for the Switch control.
 *
 * @param {'talker'|'listener'} mode
 * @returns {'talker'|'listener'}
 */
export const oppositeNavMode = mode =>
  mode === NAV_MODE_LISTENER ? NAV_MODE_TALKER : NAV_MODE_LISTENER;

/**
 * Profile write for last-used menu mode.
 *
 * @param {'talker'|'listener'} mode
 * @returns {{ protectedData: { preferred: 'talker'|'listener' } }}
 */
export const preferredNavModeData = mode => {
  const value = normalizePreferredNavMode(mode) || NAV_MODE_LISTENER;
  return {
    protectedData: { [PREFERRED_NAV_MODE_KEY]: value },
    privateData: { [PREFERRED_NAV_MODE_KEY]: value },
  };
};

/**
 * Optimistic currentUser update after a mode switch.
 *
 * @param {Object} [currentUser]
 * @param {'talker'|'listener'} mode
 * @returns {Object}
 */
export const applyPreferredNavModeToUser = (currentUser, mode) => {
  const nextMode = normalizePreferredNavMode(mode) || NAV_MODE_LISTENER;
  if (!currentUser?.attributes?.profile) {
    return currentUser;
  }
  return {
    ...currentUser,
    attributes: {
      ...currentUser.attributes,
      profile: {
        ...currentUser.attributes.profile,
        protectedData: {
          ...(currentUser.attributes.profile.protectedData || {}),
          [PREFERRED_NAV_MODE_KEY]: nextMode,
        },
        privateData: {
          ...(currentUser.attributes.profile.privateData || {}),
          [PREFERRED_NAV_MODE_KEY]: nextMode,
        },
      },
    },
  };
};
